/* eslint-disable */
import firebase from "firebase/app";
import "firebase/messaging";
import {
  FIREBASE_API_KEY,
  FIREBASE_AUTH_DOMAIN,
  FIREBASE_PROJECT_ID,
  FIREBASE_STORAGE_BUCKET,
  FIREBASE_MESSAGING_SENDER_ID,
  FIREBASE_MEASUREMENT_ID,
  FIREBASE_APP_ID,
  FIREBASE_VAPID_KEY,
} from "@/config/constant";
import authService from "@/modules/auth/services/auth";
import transportationService from "@/modules/servicePages/transportation/services/transportation";

const firebaseConfig = {
  apiKey: FIREBASE_API_KEY,
  authDomain: FIREBASE_AUTH_DOMAIN,
  projectId: FIREBASE_PROJECT_ID,
  storageBucket: FIREBASE_STORAGE_BUCKET,
  messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
  appId: FIREBASE_APP_ID,
  measurementId: FIREBASE_MEASUREMENT_ID,
};

export const initNotifications = async () => {
  try {
    if (!window.isSecureContext) {
      console.warn(
        "[FCM] Insecure context. Open the app via http://localhost (not a LAN IP) so FCM can create a token."
      );
      return null;
    }
    if (!firebase.apps.length) {
      firebase.initializeApp(firebaseConfig);
    }
    if (!firebase.messaging.isSupported()) {
      console.warn("[FCM] Firebase messaging is not supported in this browser.");
      return null;
    }
    return firebase.messaging();
  } catch (e) {
    console.warn("[FCM] Firebase messaging init failed:", e);
    return null;
  }
};

/**
 * Registers firebase-messaging-sw.js and waits until it is active.
 */
export const getMessagingServiceWorker = async () => {
  if (!("serviceWorker" in navigator)) {
    console.warn("[FCM] serviceWorker is not available.");
    return null;
  }
  const swUrl = `${process.env.BASE_URL || "/"}firebase-messaging-sw.js`;
  try {
    const registration = await navigator.serviceWorker.register(swUrl, {
      scope: "/",
    });

    // Wait until this registration has an active worker
    if (registration.installing || registration.waiting) {
      await new Promise((resolve) => {
        const worker = registration.installing || registration.waiting;
        if (!worker) {
          resolve();
          return;
        }
        if (worker.state === "activated") {
          resolve();
          return;
        }
        worker.addEventListener("statechange", () => {
          if (worker.state === "activated") resolve();
        });
      });
    }

    await navigator.serviceWorker.ready;
    console.log("[FCM] Service worker ready:", registration.scope);
    return registration;
  } catch (e) {
    console.warn("[FCM] Failed to register messaging service worker:", e);
    return null;
  }
};

/**
 * Creates a valid FCM registration token string for the backend.
 * Compatible with Firebase JS SDK v7 (usePublicVapidKey + getToken options).
 */
export const getFcmRegistrationToken = async (messaging) => {
  if (!messaging || typeof messaging.getToken !== "function") {
    console.warn("[FCM] messaging instance is missing; cannot getToken().");
    return null;
  }

  if (!window.isSecureContext) {
    console.warn(
      "[FCM] Not a secure context. Use http://localhost:8082 instead of a network IP."
    );
    return null;
  }

  if (typeof Notification === "undefined") {
    console.warn("[FCM] Notification API is not available in this context.");
    return null;
  }

  if (Notification.permission === "default") {
    const permission = await Notification.requestPermission();
    console.log("[FCM] Notification permission result:", permission);
  }

  if (Notification.permission !== "granted") {
    console.warn(
      "[FCM] Notification permission is",
      Notification.permission,
      "- allow notifications then login again."
    );
    return null;
  }

  const vapidKey = (FIREBASE_VAPID_KEY || "").trim();

  // Firebase 7: set VAPID before getToken when a project-specific key is configured
  if (vapidKey && typeof messaging.usePublicVapidKey === "function") {
    try {
      messaging.usePublicVapidKey(vapidKey);
    } catch (e) {
      // Already set after a previous getToken — safe to ignore
      console.log("[FCM] usePublicVapidKey skipped:", e && e.message ? e.message : e);
    }
  }

  const serviceWorkerRegistration = await getMessagingServiceWorker();
  if (
    serviceWorkerRegistration &&
    typeof messaging.useServiceWorker === "function"
  ) {
    try {
      messaging.useServiceWorker(serviceWorkerRegistration);
    } catch (e) {
      console.log("[FCM] useServiceWorker skipped:", e && e.message ? e.message : e);
    }
  }

  const requestToken = async (withVapid) => {
    const options = {};
    if (withVapid && vapidKey) {
      options.vapidKey = vapidKey;
    }
    if (serviceWorkerRegistration) {
      options.serviceWorkerRegistration = serviceWorkerRegistration;
    }
    return messaging.getToken(Object.keys(options).length ? options : undefined);
  };

  try {
    let token = await requestToken(true);

    if (typeof token !== "string" || !token.trim()) {
      console.warn("[FCM] getToken() returned empty value:", token);
      return null;
    }

    console.log("[FCM] Token created successfully, length:", token.trim().length);
    return token.trim();
  } catch (e) {
    const message = (e && e.message) || String(e);
    console.error("[FCM] getToken() failed:", e);

    // Wrong VAPID for this Firebase project → 401 token-subscribe-failed
    if (vapidKey && /token-subscribe-failed|authentication credential/i.test(message)) {
      console.warn(
        "[FCM] Custom VAPID key was rejected by project",
        FIREBASE_PROJECT_ID,
        "- retrying with Firebase default Web Push key. Generate the correct key from Firebase Console → Cloud Messaging → Web Push certificates."
      );
      try {
        const token = await requestToken(false);
        if (typeof token === "string" && token.trim()) {
          console.log("[FCM] Token created with default VAPID, length:", token.trim().length);
          return token.trim();
        }
      } catch (retryErr) {
        console.error("[FCM] Retry without custom VAPID also failed:", retryErr);
      }
    }

    return null;
  }
};

/**
 * Resolves an FCM token for login.
 * Tries to create a fresh token (time-boxed), then falls back to cache.
 */
export const resolveFcmTokenForLogin = async (
  messaging,
  timeoutMs = 10000,
  { preferFresh = true } = {}
) => {
  const readCache = () => {
    const cached = localStorage.getItem("fcmToken");
    return typeof cached === "string" && cached.trim() ? cached.trim() : null;
  };

  if (!preferFresh) {
    const cached = readCache();
    if (cached) return cached;
  }

  let messagingInstance = messaging;
  if (!messagingInstance) {
    try {
      messagingInstance = await initNotifications();
    } catch (e) {
      console.warn("[FCM] initNotifications failed during login:", e);
    }
  }

  if (messagingInstance) {
    try {
      const fresh = await Promise.race([
        getFcmRegistrationToken(messagingInstance),
        new Promise((resolve) => setTimeout(() => resolve(null), timeoutMs)),
      ]);
      if (fresh) {
        localStorage.setItem("fcmToken", fresh);
        return fresh;
      }
    } catch (e) {
      console.warn("[FCM] Could not create a fresh token before login:", e);
    }
  }

  const cached = readCache();
  if (cached) {
    console.log("[FCM] Using cached fcmToken for login payload");
    return cached;
  }

  console.warn(
    "[FCM] No fcm_token available for login payload. Allow notifications on http://localhost."
  );
  return null;
};

/**
 * After login: ensure token exists and PATCH backends. Never block the login UI.
 */
export const syncFcmTokenInBackground = (messaging) => {
  Promise.resolve()
    .then(async () => {
      const token = await resolveFcmTokenForLogin(messaging, 15000, {
        preferFresh: true,
      });
      if (token) {
        await sendFcmTokenAfterLogin(token);
      }
    })
    .catch((e) => {
      console.warn("[FCM] Background token sync failed:", e);
    });
};

/**
 * Persists FCM token locally and PATCHes it to SSO + transportation backends.
 */
export const sendFcmTokenAfterLogin = async (token) => {
  if (typeof token !== "string" || !token.trim()) {
    return;
  }
  const fcmToken = token.trim();
  localStorage.setItem("fcmToken", fcmToken);

  await Promise.all([
    authService.sendFirebase(fcmToken),
    transportationService.updateFcmToken(fcmToken),
  ]);
};
